import { FilterOutlined } from "@ant-design/icons";
import { Badge, Button, Flex, Modal } from "antd";
import Search from "antd/es/input/Search";
import { FC, useState } from "react";
import {
    ContractProduct,
    ContractType,
    DiscountProduct,
} from "tm-shared/types";

import { FilterDrawerUI } from "@/components/Filters/FilterDrawerUI";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";
import { useDebounce } from "@/hooks/useDebounce";
import { useFilters } from "@/hooks/useFilters";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useQueryFilter } from "@/hooks/useQueryFilter";

import { CheckboxProductRow } from "./CheckboxProductRow";
import {
    StyledContentBody,
    StyledContentList,
    StyledContentListContainer,
} from "./Styled";

interface AddProductsModalProps {
    contractType: ContractType.PaidProductInput;
    open: boolean;
    onClose: () => void;
    selectedProducts: ContractProduct[];
    onAdd: (product: ContractProduct) => void;
    onRemove: (barcode: string) => void;
}

interface AddBarcodesModalProps {
    contractType: ContractType.AssortmentMatrix;
    open: boolean;
    onClose: () => void;
    selectedProducts: string[];
    onAdd: (barcode: string) => void;
    onRemove: (barcode: string) => void;
}

interface AddDiscountsModalProps {
    contractType: ContractType.Discount;
    open: boolean;
    onClose: () => void;
    selectedProducts: DiscountProduct[];
    onAdd: (product: DiscountProduct) => void;
    onRemove: (barcode: string) => void;
}

export const AddProductsModal: FC<
    AddProductsModalProps | AddBarcodesModalProps | AddDiscountsModalProps
> = ({ contractType, onClose, open, selectedProducts, onAdd, onRemove }) => {
    const { products } = useAppSelector((s) => s.offline);

    const [query, setQuery] = useState("");
    const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

    const [filters, setFilters] = useState<{
        [key: string]: (string | number | boolean)[];
    }>({});

    const isProductSelected = (barcode: string) => {
        switch (contractType) {
            case ContractType.PaidProductInput:
            case ContractType.Discount:
                return selectedProducts.some((p) => p.barcode === barcode);
            case ContractType.AssortmentMatrix:
                return selectedProducts.some((p) => p === barcode);
        }
    };

    const handleCheckboxChange = (
        product: ContractProduct,
        checked: boolean,
    ) => {
        if (checked) {
            switch (contractType) {
                case ContractType.PaidProductInput:
                    onAdd(product);
                    break;
                case ContractType.AssortmentMatrix:
                    onAdd(product.barcode);
                    break;
                case ContractType.Discount:
                    onAdd({
                        barcode: product.barcode,
                        price: 0,
                    });
            }
        } else {
            onRemove(product.barcode);
        }
    };

    const { filteredProducts, availableFilters } = useFilters({
        products,
        filters: filters,
    });

    const debouncedQuery = useDebounce(query);

    const searchFilteredProducts = useQueryFilter(
        filteredProducts,
        debouncedQuery,
        [(p) => p.name, (p) => p.barcode],
    );
    const { visibleItems, loadMore } = useInfiniteScroll(
        searchFilteredProducts,
    );

    const onCloseFilterDrawer = () => setOpenFilterDrawer(false);

    const activeFiltersCount = Object.values(filters).filter(
        (values) => values.length > 0,
    ).length;

    return (
        <>
            <Modal
                title={"Додати товар"}
                open={open}
                onCancel={onClose}
                centered={true}
                footer={[]}
                maskClosable={false}
                styles={{ body: { height: "90vh" }, footer: { margin: 0 } }}
            >
                <StyledContentBody>
                    <Flex gap={8} align="center">
                        <Search
                            placeholder="Пошук"
                            allowClear
                            size="large"
                            onChange={(evt) => setQuery(evt.target.value)}
                        />

                        <Badge count={activeFiltersCount}>
                            <Button
                                onClick={() => setOpenFilterDrawer(true)}
                                type="text"
                                icon={
                                    <FilterOutlined style={{ fontSize: 24 }} />
                                }
                            />
                        </Badge>
                    </Flex>

                    <StyledContentListContainer>
                        {visibleItems.length > 0 ? (
                            <StyledContentList vertical gap={16}>
                                {visibleItems.map((product, index) => {
                                    const isChecked = isProductSelected(
                                        product.barcode,
                                    );
                                    const isLast =
                                        index === visibleItems.length - 1;

                                    return (
                                        <CheckboxProductRow
                                            key={product.barcode}
                                            checked={isChecked}
                                            product={product}
                                            onChange={handleCheckboxChange}
                                            isLast={isLast}
                                            onInView={() => loadMore()}
                                        />
                                    );
                                })}
                            </StyledContentList>
                        ) : (
                            <PrimaryText>Товарів не знайдено</PrimaryText>
                        )}
                    </StyledContentListContainer>
                </StyledContentBody>
            </Modal>

            <FilterDrawerUI
                isOpen={openFilterDrawer}
                onClose={onCloseFilterDrawer}
                onApply={onCloseFilterDrawer}
                onReset={() => setFilters({})}
                availableFilters={availableFilters}
                selectedFilters={filters}
                products={products}
                availableProductsLength={filteredProducts.length}
                onFilterChange={(
                    tag: string,
                    values: (string | number | boolean)[],
                ) => {
                    setFilters((prev) => ({
                        ...prev,
                        [tag]: values,
                    }));
                }}
            />
        </>
    );
};
